import { useCallback } from 'react';
import { useSelectedItemProps } from './useSelectedItemProps';
import { imageFormProp, mediaMetaFromProps, mediaMetaPatch } from '../../utils/imageSlots';

/**
 * Paliers de résolution rangés à côté de la prop média du bloc.
 *
 * L'URL du bloc reste une chaîne — c'est la forme déjà en production, et un
 * bloc web ne renomme ni ne reshape ses props. Ce qu'une URL ne dit pas (l'id
 * du média, le palier retenu, le `srcset`) vit dans trois props voisines,
 * dérivées du nom du champ : `srcMediaId`, `srcResolution`, `srcSrcset` pour un
 * champ `src`.
 *
 * Une image branchée sur une variable du CRM porte en plus son triplet dans
 * `srcVariable` (`variableImageProp`) ; toute autre source le détache.
 *
 * Ses réglages de formulaire vivent dans `srcForm` (`imageFormProp`).
 *
 * Les props partent en une seule écriture : le `onChange` d'un champ
 * Puck ne couvre que sa propre prop, et deux dispatch successifs partiraient du
 * même état figé.
 */
export function useMediaMetaProps(name, { focalPointProp } = {}) {
  const { itemProps, updateItemProps } = useSelectedItemProps();

  const meta = mediaMetaFromProps(itemProps, name);

  const onMediaChange = useCallback((next) => {
    updateItemProps(mediaMetaPatch(name, next));
  }, [name, updateItemProps]);

  const formProp = imageFormProp(name);
  const form = itemProps[formProp];
  const onFormChange = useCallback((next) => {
    updateItemProps({ [formProp]: next });
  }, [formProp, updateItemProps]);

  const onFocalPointChange = useCallback((next) => {
    updateItemProps({ [focalPointProp]: next });
  }, [focalPointProp, updateItemProps]);

  return {
    meta, onMediaChange, form, onFormChange,
    ...(focalPointProp && itemProps.mediaFit !== 'contain'
      ? { focalPoint: itemProps[focalPointProp], onFocalPointChange } : {}),
  };
}
